import { useSessionStorage } from '@app/_shared/hooks/useSessionStorage';
import { useCallback, useMemo } from 'react';

/**
 * Tracks whether the maintenance banner has been dismissed for its current message and end time.
 * The dismissal persists in `sessionStorage` for the rest of the browser session.
 *
 * @param message - The banner's current display message.
 * @param endTime - The banner's current end time.
 * @returns Whether the banner is dismissed, and a function to dismiss it.
 */
export function useMaintenanceBannerState(
    message: string,
    endTime: Date,
): {
    isDismissed: boolean;
    dismiss: () => void;
} {
    // The banner's identity is its message and end time.
    // Recomputing `bannerKey` when either changes means an updated banner is treated as new.
    // A dismissal of the old banner doesn't suppress the new one.
    // StockBlocks and Shell share an origin, so they share sessionStorage too: StockBlocks'
    // MaintenanceBannerConfig::dismissKey() must compute this exact same string (including using
    // millisecond, not second, precision) or a dismissal on one site won't be honored on the other.
    const bannerKey = useMemo(() => `${endTime.getTime()}-hide-maintenance-banner-${message}`, [endTime, message]);
    // The server can never read sessionStorage.
    // Seeding the snapshot with bannerKey makes the server, and the client's first hydration render, assume the banner is dismissed.
    // The banner then opts into showing only once the client confirms sessionStorage really doesn't have it dismissed.
    // The opposite default — assuming not dismissed — flashes a previously-dismissed banner back into view on load.
    // The client then corrects it back to hidden a moment later.
    const [previouslyDismissedBanner, setPreviouslyDismissedBanner] = useSessionStorage(
        'dismissed-maintenance-banner',
        bannerKey,
    );

    const dismiss = useCallback(() => {
        setPreviouslyDismissedBanner(bannerKey);
    }, [bannerKey, setPreviouslyDismissedBanner]);

    const isDismissed = previouslyDismissedBanner === bannerKey;
    return { isDismissed, dismiss };
}
